import { useEffect, useState, useMemo } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import {
	ChevronRight,
	ChevronDown,
	LayoutDashboard,
	TicketCheck,
	Archive,
	Search,
} from "lucide-react";
import { MenuItemButton } from "@/components/src/menu-item-button/menu-item-button.component";
import { useVendorJobStore } from "../../store/VendorJobs";
import {
	ChannelListItem,
	ChannelListItemContent,
	JobListItem,
} from "../../components/job-list/job-list-components/job-list-item.component";
import { VendorFileListItem } from "./vendor-file-list-item.component";
import { VendorSharedFile } from "@/lib/types/job-list.types";
import { JobStatusEnum } from "@/lib/types/job";
import { Loading } from "@/components/src/animations/loading";

interface VendorJobListProps {
	vendorOrgId: string;
	customerOrgId: string;
	onOpenSearch?: () => void;
}

const DOC_TYPE_LABELS: Record<string, string> = {
	qt: "Kostenvoranschläge",
	dn: "Lieferscheine",
	cn: "Gutschriften",
};

const DOC_TYPES = ["qt", "dn", "cn"] as const;

export const VendorJobList: React.FC<VendorJobListProps> = ({
	vendorOrgId,
	customerOrgId,
	onOpenSearch,
}) => {
	const navigate = useNavigate();
	const location = useLocation();
	const requestedChannels = useVendorJobStore(
		(state) => state.requestedChannels
	);
	const deniedChannels = useVendorJobStore((state) => state.deniedChannels);
	const acceptedJobs = useVendorJobStore((state) => state.acceptedJobs);
	const sharedFiles = useVendorJobStore((state) => state.sharedFiles);
	const loading = useVendorJobStore((state) => state.loading);
	const fetchVendorData = useVendorJobStore((state) => state.fetchVendorData);
	const getGroupedFiles = useVendorJobStore((state) => state.getGroupedFiles);

	useEffect(() => {
		fetchVendorData(vendorOrgId, customerOrgId);
	}, [vendorOrgId, customerOrgId, fetchVendorData]);

	const { jobId } = useParams<{ jobId: string }>();
	const activeJobId = jobId ? Number(jobId) : null;

	const grouped = useMemo(
		() => getGroupedFiles(),
		[sharedFiles, getGroupedFiles]
	);

	const activeFileId = useVendorJobStore((state) => state.activeFileId);

	const activeDocType = useMemo(() => {
		const allFiles = [
			...Object.values(grouped.inArbeit),
			...Object.values(grouped.abgeschlossen),
		].flat();

		if (activeFileId) {
			const file = allFiles.find((f) => f.file_id === activeFileId);
			if (file) return file.document_type;
		}

		if (activeJobId == null) return null;
		const jobFiles = allFiles.filter((f) => f.job_id === activeJobId);
		const types = new Set(jobFiles.map((f) => f.document_type));
		if (types.has("cn")) return "cn";
		if (types.has("dn")) return "dn";
		if (types.has("qt")) return "qt";
		return null;
	}, [activeFileId, activeJobId, grouped]);

	if (loading) {
		return (
			<div className="pt-4">
				<Loading />
			</div>
		);
	}

	return (
		<div className="flex flex-col gap-0.5 pt-2 text-sm h-full overflow-y-auto">
			{onOpenSearch && (
				<MenuItemButton
					onClick={onOpenSearch}
					style={{
						display: "flex",
						flexDirection: "row",
						justifyContent: "start",
						alignItems: "center",
						gap: "8px",
					}}
					dataTestId="vendor-job-list-search-button"
				>
					<Search className="w-4 h-4" />
					<span className="text-sm">Suche</span>
					<span className="ml-auto text-[10px] text-stone-400">
						{/(Mac|iPhone|iPod|iPad)/i.test(navigator.userAgent)
							? "⌘K"
							: "Strg+K"}
					</span>
				</MenuItemButton>
			)}
			<MenuItemButton
				style={{
					display: "flex",
					flexDirection: "row",
					justifyContent: "start",
					alignItems: "center",
					gap: "8px",
				}}
				selected={location.pathname === `/vendor/${vendorOrgId}`}
				onClick={() => navigate(`/vendor/${vendorOrgId}`)}
				aria-label="Übersicht"
				data-testid="vendor-nav-dashboard"
			>
				<LayoutDashboard className="w-4 h-4" />
				<span className="text-sm">Übersicht</span>
			</MenuItemButton>
			<SectionHeading
				label="Eingegangen"
				testId="vendor-list-heading-incoming"
			/>
			<CollapsibleSection
				label="Angefragt"
				count={requestedChannels.length}
				testId="vendor-list-section-requested"
				defaultOpen={false}
			>
				{requestedChannels.map((channel) => (
					<ChannelListItem
						key={channel.channelId}
						channelId={channel.channelId}
						entityId={vendorOrgId}
						basePath="/vendor"
					>
						<ChannelListItemContent channel={channel} />
					</ChannelListItem>
				))}
			</CollapsibleSection>

			<CollapsibleSection
				label="Akzeptiert"
				count={acceptedJobs.length}
				testId="vendor-list-section-accepted"
				defaultOpen={false}
				forceOpen={
					activeJobId != null &&
					activeDocType == null &&
					acceptedJobs.some((j) => j.id === activeJobId)
				}
			>
				{acceptedJobs.map((job) => (
					<JobListItem
						key={job.id}
						basePath="/vendor"
						entityId={vendorOrgId}
						jobId={job.id ?? 0}
						jobStatus={job.status as unknown as JobStatusEnum}
						jobTitle={job.title ?? ""}
						jobCode={job.code ?? ""}
						patientId={job.patient_id ?? ""}
						isSelected={activeJobId === (job.id ?? 0)}
					/>
				))}
			</CollapsibleSection>

			<SectionHeading
				label="Offen"
				testId="vendor-list-heading-in-progress"
			/>
			{DOC_TYPES.map((dt) => (
				<FileSubSection
					key={`in-progress-${dt}`}
					files={grouped.inArbeit[dt]}
					label={DOC_TYPE_LABELS[dt]}
					vendorOrgId={vendorOrgId}
					testId={`vendor-list-section-in-progress-${dt}`}
					activeJobId={activeDocType === dt ? activeJobId : null}
				/>
			))}

			<SectionHeading
				label="Verrechnet (12 Monate)"
				testId="vendor-list-heading-completed"
			/>
			{DOC_TYPES.map((dt) => (
				<FileSubSection
					key={`completed-${dt}`}
					files={grouped.abgeschlossen[dt]}
					label={DOC_TYPE_LABELS[dt]}
					vendorOrgId={vendorOrgId}
					testId={`vendor-list-section-completed-${dt}`}
					activeJobId={activeDocType === dt ? activeJobId : null}
				/>
			))}
			<CollapsibleSection
				label="Anfrage abgelehnt"
				count={deniedChannels.length}
				testId="vendor-list-section-denied"
				defaultOpen={false}
			>
				{deniedChannels.map((channel) => (
					<ChannelListItem
						key={channel.channelId}
						channelId={channel.channelId}
						entityId={vendorOrgId}
						basePath="/vendor"
					>
						<ChannelListItemContent channel={channel} />
					</ChannelListItem>
				))}
			</CollapsibleSection>

			<VendorPagesList vendorOrgId={vendorOrgId} />
		</div>
	);
};

const VendorPagesList: React.FC<{ vendorOrgId: string }> = ({
	vendorOrgId,
}) => {
	const navigate = useNavigate();
	const location = useLocation();

	return (
		<div className="flex flex-col gap-1 pt-2">
			<MenuItemButton
				style={{
					display: "flex",
					flexDirection: "row",
					justifyContent: "start",
					alignItems: "center",
					gap: "8px",
				}}
				selected={location.pathname.includes("/verrechnet")}
				onClick={() => navigate(`/vendor/${vendorOrgId}/verrechnet`)}
				aria-label="Alle verrechneten Dateien anzeigen"
				data-testid="vendor-nav-verrechnet"
			>
				<TicketCheck className="w-4 h-4" />
				<span className="text-sm">Verrechnet</span>
			</MenuItemButton>
			<MenuItemButton
				style={{
					display: "flex",
					flexDirection: "row",
					justifyContent: "start",
					alignItems: "center",
					gap: "8px",
				}}
				selected={location.pathname.includes("/archiv")}
				onClick={() => navigate(`/vendor/${vendorOrgId}/archiv`)}
				aria-label="Archivierte Dateien anzeigen"
				data-testid="vendor-nav-archiv"
			>
				<Archive className="w-4 h-4" />
				<span className="text-sm">Archiv</span>
			</MenuItemButton>
		</div>
	);
};

const SectionHeading: React.FC<{ label: string; testId: string }> = ({
	label,
	testId,
}) => (
	<div
		className="px-2 pt-3 pb-1 text-xs font-semibold text-stone-400 uppercase tracking-wide"
		data-testid={testId}
	>
		{label}
	</div>
);

const CountBadge: React.FC<{ count: number; testId: string }> = ({
	count,
	testId,
}) => (
	<span
		className="ml-1 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-stone-100 text-stone-500 text-[11px] font-medium"
		data-testid={testId}
	>
		{count}
	</span>
);

const CollapsibleSection: React.FC<{
	label: string;
	count: number;
	testId: string;
	defaultOpen: boolean;
	forceOpen?: boolean;
	children: React.ReactNode;
}> = ({ label, count, testId, defaultOpen, forceOpen, children }) => {
	const [open, setOpen] = useState(defaultOpen);

	useEffect(() => {
		if (forceOpen) setOpen(true);
	}, [forceOpen]);

	const handleToggle = () => setOpen((prev) => !prev);

	return (
		<div data-testid={testId}>
			<div
				className="flex items-center gap-1 px-1 py-1 pl-4 cursor-pointer hover:bg-stone-50 rounded"
				onClick={handleToggle}
				aria-expanded={open}
				aria-label={`${label} ${count} Einträge`}
			>
				{open ? (
					<ChevronDown className="w-3.5 h-3.5 text-stone-400 shrink-0" />
				) : (
					<ChevronRight className="w-3.5 h-3.5 text-stone-400 shrink-0" />
				)}
				<span className="text-stone-700">{label}</span>
				<CountBadge
					count={count}
					testId={`vendor-list-count-${testId.replace(
						"vendor-list-section-",
						""
					)}`}
				/>
			</div>
			{open && (
				<div className="max-h-[50vh] overflow-y-auto">{children}</div>
			)}
		</div>
	);
};

const FileSubSection: React.FC<{
	files: VendorSharedFile[];
	label: string;
	vendorOrgId: string;
	testId: string;
	activeJobId?: number | null;
}> = ({ files, label, vendorOrgId, testId, activeJobId }) => {
	const containsActiveJob =
		activeJobId != null && files.some((f) => f.job_id === activeJobId);
	const [manualToggle, setManualToggle] = useState<boolean | null>(null);
	const open = manualToggle ?? containsActiveJob;

	useEffect(() => {
		setManualToggle(null);
	}, [containsActiveJob]);

	const handleToggle = () =>
		setManualToggle((prev) => !(prev ?? containsActiveJob));

	return (
		<div data-testid={testId}>
			<div
				className="flex items-center gap-1 px-1 py-1 pl-4 cursor-pointer hover:bg-stone-50 rounded"
				onClick={handleToggle}
				aria-expanded={open}
				aria-label={`${label} ${files.length} Dateien`}
			>
				{open ? (
					<ChevronDown className="w-3.5 h-3.5 text-stone-400 shrink-0" />
				) : (
					<ChevronRight className="w-3.5 h-3.5 text-stone-400 shrink-0" />
				)}
				<span className="text-stone-700">{label}</span>
				<CountBadge
					count={files.length}
					testId={`vendor-list-count-${testId.replace(
						"vendor-list-section-",
						""
					)}`}
				/>
			</div>
			{open && (
				<div className="max-h-[50vh] overflow-y-auto">
					{files.map((file) => (
						<VendorFileListItem
							key={file.file_id}
							file={file}
							vendorOrgId={vendorOrgId}
						/>
					))}
				</div>
			)}
		</div>
	);
};
